import {Button, DatePicker, Empty, Select} from "antd";
import {LeftOutlined, RightOutlined} from "@ant-design/icons";
import dayjs, {Dayjs} from "dayjs";
import React, {useMemo} from "react";
import {Holiday} from "../../../domains/models/holiday";
import {MONTH_NAMES, renderHolidayType} from "../holiday.view";


interface HolidayCalendarViewProps {
    holidays: Holiday[];
    selectedDate: Dayjs;
    onSelectedDateChange: (value: Dayjs) => void;
    setSelectedHoliday: (value: Holiday | null) => void;
}

const HolidayCalendarView = (props: HolidayCalendarViewProps) => {
    const calendarDays = useMemo(() => {
        const startOfMonth = props.selectedDate.startOf("month");
        const gridStart = startOfMonth.startOf("week");
        return Array.from({length: 42}, (_, index) => gridStart.add(index, "day"));
    }, [props.selectedDate]);

    const holidaysByDate = useMemo(() => {
        return props.holidays.reduce<Record<string, Holiday[]>>((acc, holiday) => {
            const dateKey = dayjs(holiday.date).format("YYYY-MM-DD");
            acc[dateKey] = [...(acc[dateKey] || []), holiday];
            return acc;
        }, {});
    }, [props.holidays]);

    const currentMonthHolidays = useMemo(() => {
        return props.holidays.filter((holiday) => dayjs(holiday.date).isSame(props.selectedDate, "month"));
    }, [props.holidays, props.selectedDate]);

    const changeMonth = (direction: "previous" | "next") => {
        props.onSelectedDateChange(
            direction === "previous" ? props.selectedDate.subtract(1, "month") : props.selectedDate.add(1, "month")
        );
    };

    return (
        <section className="pn-holiday__calendar-shell">
            <div className="pn-holiday__calendar-toolbar">
                <div className="pn-holiday__calendar-navigation">
                    <div className={"pn-holiday-calendar-header-button"}>
                        <Button
                            icon={<LeftOutlined/>}
                            className="pn-holiday__icon-button"
                            onClick={() => changeMonth("previous")}
                        />
                        <DatePicker
                            picker="year"
                            value={props.selectedDate}
                            allowClear={false}
                            className="pn-holiday__year-picker"
                            onChange={(value) => value && props.onSelectedDateChange(props.selectedDate.year(value.year()))}
                        />
                        <Select
                            value={props.selectedDate.month()}
                            className="pn-holiday__month-picker"
                            onChange={(month) => props.onSelectedDateChange(props.selectedDate.month(month))}
                        >
                            {MONTH_NAMES.map((month, index) => (
                                <Select.Option key={month} value={index}>{month}</Select.Option>
                            ))}
                        </Select>
                        <Button
                            icon={<RightOutlined/>}
                            className="pn-holiday__icon-button"
                            onClick={() => changeMonth("next")}
                        />
                    </div>

                </div>
                <Button onClick={() => props.onSelectedDateChange(dayjs())}>Today</Button>
            </div>

            <div className="pn-holiday__calendar">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((weekday) => (
                    <div className="pn-holiday__calendar-weekday" key={weekday}>{weekday}</div>
                ))}
                {calendarDays.map((date) => {
                    const dateKey = date.format("YYYY-MM-DD");
                    const dayHolidays = holidaysByDate[dateKey] || [];
                    const isCurrentMonth = date.isSame(props.selectedDate, "month");
                    const isToday = date.isSame(dayjs(), "day");

                    return (
                        <button
                            key={dateKey}
                            className={[
                                "pn-holiday__calendar-cell",
                                !isCurrentMonth ? "pn-holiday__calendar-cell--muted" : "",
                                isToday ? "pn-holiday__calendar-cell--today" : "",
                                dayHolidays.length ? "pn-holiday__calendar-cell--has-holiday" : "",
                            ].filter(Boolean).join(" ")}
                            onClick={() => dayHolidays[0] && props.setSelectedHoliday(dayHolidays[0])}
                        >
                            <span className="pn-holiday__calendar-date">{date.date()}</span>
                            <div className="pn-holiday__calendar-events">
                                {dayHolidays.map((holiday) => (
                                    <span
                                        key={holiday.id || `${holiday.name}-${holiday.date}`}
                                        className={`pn-holiday__calendar-event pn-holiday__calendar-event--${holiday.type.toLowerCase()}`}
                                    >
                                        {holiday.name}
                                    </span>
                                ))}
                            </div>
                        </button>
                    );
                })}
            </div>

            <aside className="pn-holiday__month-agenda">
                <h4 className="pn-holiday__section-title">{props.selectedDate.format("MMMM YYYY")} Holidays</h4>
                {currentMonthHolidays.length ? currentMonthHolidays.map((holiday) => (
                    <button
                        key={holiday.id || `${holiday.name}-${holiday.date}`}
                        className="pn-holiday__agenda-item"
                        onClick={() => props.setSelectedHoliday(holiday)}
                    >
                        <span className="pn-holiday__agenda-date">{dayjs(holiday.date).format("DD MMM")}</span>
                        <span className="pn-holiday__agenda-name">{holiday.name}</span>
                        {renderHolidayType(holiday.type)}
                    </button>
                )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No holidays this month"/>}
            </aside>
        </section>
    )
};

export default HolidayCalendarView;